/* eslint-disable no-console */
import React, { useCallback, useEffect, useState } from "react";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import CustomModal from "../../Core/Components/Modal/CustomModal";
import ChargerIcon from "../../Common/Svgs/ChargerIcon";
import CloseIcon from "../../Common/Svgs/CloseIcon";
import { client } from "../../Utils/axiosClient";
import { updateRental } from "../../Redux/Slices/rentals.slice";

const seedOriginalColumns = [
  "Hub Station",
  "Registration no.",
  "Start Energy",
  "End Energy",
  "Operator",
];

const seedSwappedColumns = [
  "Swapped At",
  "Registration no.",
  "Start Energy",
  "End Energy",
  "Operator",
];

const ViewBatteryEnergyModal = ({ visible, onCancel }) => {
  const dispatch = useDispatch();

  const { currentRental } = useSelector((state) => state.rentals);
  const { allSystemUsers } = useSelector((state) => state.loggedInUser);

  const [original, setOriginal] = useState([]);
  const [swapped, setSwapped] = useState([]);
  const [originalColumns, setOriginalColumns] = useState([]);
  const [swappedColumns, setSwappedColumns] = useState([]);

  const handleCancel = () => {
    dispatch(updateRental({ key: "currentRental", value: {} }));
    onCancel();
  };

  // get the operator details based on the submittedBy/updatedBy id
  const getOperator = (id) => {
    const user = allSystemUsers?.find((u) => u.id === +id);
    if (user) return `${user.firstName} ${user.lastName}`;
    return `--`;
  };

  // get the original and swapped battery details
  const getBatteryDetails = useCallback(async () => {
    try {
      const response = await client.get(
        `${process.env.REACT_APP_API_URL}/rentals/completed-battery-energy`,
        {
          params: {
            rentalId: currentRental.id,
          },
        },
      );

      if (response.status === 200) {
        const { original: originalBatteries, swapped: swappedBatteries } =
          response.data;

        setOriginal(originalBatteries);

        if (swappedBatteries.length) {
          setOriginalColumns([
            "Hub Station",
            "Registration no.",
            "Start Energy",
            "End Energy",
            "Swapped At",
            "Operator",
          ]);

          setSwappedColumns([
            "Swapped At",
            "Registration no.",
            "Start Energy",
            "End Energy",
            "Ended At",
            "Operator",
          ]);

          setSwapped(swappedBatteries);
        } else {
          setOriginalColumns(seedOriginalColumns);
          setSwappedColumns(seedSwappedColumns);
        }
      }
    } catch (error) {
      console.log(error);
    }
  }, [currentRental]);

  useEffect(() => {
    if (visible) getBatteryDetails();
  }, [visible, getBatteryDetails]);

  return (
    <CustomModal visible={visible} onCancel={handleCancel} width="1121px">
      <div
        className="py-4"
        style={{
          boxShadow:
            "12px 12px 16px -4px rgba(16, 24, 40, 0.08), -4px -4px 6px -2px rgba(16, 24, 40, 0.03)",
        }}
      >
        <div className="px-4 flex items-center justify-between">
          <div className="flex items-center gap-x-3">
            <div className="flex items-center justify-center w-12 h-12 bg-green rounded-full">
              <ChargerIcon size={24} className="text-yellow" />
            </div>

            <p className="font-poppins text-base font-medium">
              View Battery Energy
            </p>
          </div>

          <button type="button" onClick={handleCancel}>
            <CloseIcon className="w-6 h-6 stroke-dark-gray" />
          </button>
        </div>

        <div>
          <p className="px-4 pt-6 text-xs font-poppins font-medium">
            Original Batteries
          </p>

          <div className="mt-2 flex items-center bg-background py-3 border-b-[0.5px] border-light-grey">
            {originalColumns.map((item) => (
              <p
                key={item}
                className="flex-1 px-6 text-10 font-aileron font-normal text-dark-gray"
              >
                {item}
              </p>
            ))}
          </div>

          {original.map((item) => {
            const submittedBy = getOperator(+item.updatedBy);

            const submittedAt = item?.submittedAt
              ? dayjs(item.submittedAt).format("DD MMM, YYYY, hh:mm a")
              : "--";

            return (
              <div
                key={item.batteryId}
                className="flex items-center py-[11px] border-b border-light-grey"
              >
                <div className="flex-1 flex items-center">
                  <p className="flex-1 px-6 text-xs font-aileron font-semibold capitalize">
                    {item?.originalHubName}
                  </p>

                  <p className="flex-1 px-6 text-xs font-aileron font-semibold uppercase">
                    {item?.registrationNo}
                  </p>

                  <div className="flex-1 px-6 flex items-center">
                    <ChargerIcon size={16} className="text-green" />
                    <p className="ml-2 font-aileron text-10 font-semibold text-green">
                      {item?.startEnergy} kWh
                    </p>
                  </div>

                  <div className="flex-1 px-6 flex items-center">
                    <ChargerIcon size={16} className="text-green" />
                    <p className="ml-2 font-aileron text-10 font-semibold text-green">
                      {item?.endEnergy} kWh
                    </p>
                  </div>

                  {item?.swappedHubName ? (
                    <p className="flex-1 px-6 text-xs font-aileron font-semibold capitalize">
                      {item?.swappedHubName}
                    </p>
                  ) : null}

                  <div className="flex-1 px-6">
                    <p className="font-aileron text-xs font-semibold text-dark-gray">
                      {submittedBy}
                    </p>

                    <p className="font-aileron text-10 font-semibold text-dark-gray">
                      {submittedAt}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}

          {swapped.length ? (
            <div>
              <p className="px-4 pt-6 text-xs font-poppins font-medium">
                Swapped Batteries
              </p>

              <div className="mt-2 flex items-center bg-background py-3 border-b-[0.5px] border-light-grey">
                {swappedColumns.map((item) => (
                  <p
                    key={item}
                    className="flex-1 px-6 text-10 font-aileron font-normal text-dark-gray"
                  >
                    {item}
                  </p>
                ))}
              </div>

              {swapped?.map((item) => {
                const updatedBy = getOperator(item?.updatedBy);

                const updatedAt = item?.updatedAt
                  ? dayjs(item.updatedAt).format("DD MMM, YYYY, hh:mm a")
                  : "--";

                return (
                  <div
                    key={item.batteryId}
                    className="flex items-center py-[11px] border-b border-light-grey"
                  >
                    <div className="flex-1 flex items-center">
                      <p className="flex-1 px-6 text-xs font-aileron font-semibold capitalize">
                        {item?.swappedHubName}
                      </p>

                      <p className="flex-1 px-6 text-xs font-aileron font-semibold uppercase">
                        {item?.registrationNo}
                      </p>

                      <div className="flex-1 px-6 flex items-center">
                        <ChargerIcon size={16} className="text-green" />
                        <p className="ml-2 font-aileron text-10 font-semibold text-green">
                          {item?.startEnergy} kWh
                        </p>
                      </div>

                      <div className="flex-1 px-6 flex items-center">
                        <ChargerIcon size={16} className="text-green" />
                        <p className="ml-2 font-aileron text-10 font-semibold text-green">
                          {item?.endEnergy} kWh
                        </p>
                      </div>

                      <p className="flex-1 px-6 text-xs font-aileron font-semibold capitalize">
                        {item?.originalHubName}
                      </p>

                      <div className="flex-1 px-6">
                        <p className="font-aileron text-xs font-semibold text-dark-gray">
                          {updatedBy}
                        </p>

                        <p className="font-aileron text-10 font-semibold text-dark-gray">
                          {updatedAt}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : null}
        </div>
      </div>
    </CustomModal>
  );
};

export default ViewBatteryEnergyModal;
